import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import type { UserSummary } from '@shared/types';
import { useAuth } from '@/app/auth-context';
import { Button, Input, Label } from '@/components/ui';
import { apiFetch, getErrorMessage } from '@/lib/api';
import { routes } from '@/lib/routes';
import { usePageMeta } from '@/lib/seo';

export function PasswordSetupPage() {
  const { user, refresh } = useAuth();
  const arabic = user?.preferredLanguage === 'ar';
  const text = (english: string, arabicText: string) => (arabic ? arabicText : english);
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<{ user: UserSummary }>('/api/auth/complete-password', {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),
    onSuccess: async ({ user: updated }) => {
      await refresh();
      void navigate(
        updated.onboardingCompleted ? `${routes.profile}?from=intake` : routes.onboarding,
        { replace: true },
      );
    },
  });
  usePageMeta({
    title: text('Protect your account', 'احمِ حسابك'),
    description: text('Choose your private password.', 'اختار كلمة سر خاصة بيك.'),
    noIndex: true,
  });
  if (user && !user.mustChangePassword)
    return <Navigate to={user.onboardingCompleted ? routes.profile : routes.onboarding} replace />;
  const mismatch = confirm.length > 0 && password !== confirm;
  const invalid =
    password.length < 10 ||
    !/[a-z]/u.test(password) ||
    !/[A-Z]/u.test(password) ||
    !/[0-9]/u.test(password);
  return (
    <div className="auth-card" dir={arabic ? 'rtl' : 'ltr'}>
      <span className="auth-card__eyebrow">{text('Final security step', 'آخر خطوة للأمان')}</span>
      <h1>{text('Choose your private password', 'اختار كلمة سر خاصة بيك')}</h1>
      <p>
        {text(
          'This account was created for you. Choose a private password before using the CV tools.',
          'الحساب ده اتعمل ليك. اختار كلمة سر خاصة بيك قبل ما تستخدم أدوات السيرة.',
        )}
      </p>
      <div className="field-stack mt-5">
        <Label htmlFor="new-password">{text('New password', 'كلمة السر الجديدة')}</Label>
        <Input
          id="new-password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <p className="field-help">
          {text(
            'At least 10 characters with uppercase, lowercase, and a number.',
            '10 حروف على الأقل، وفيها حرف إنجليزي كبير وصغير ورقم.',
          )}
        </p>
      </div>
      <div className="field-stack">
        <Label htmlFor="confirm-password">{text('Confirm password', 'أكد كلمة السر')}</Label>
        <Input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
      </div>
      {mismatch ? (
        <p className="form-error" role="alert">
          {text('The passwords do not match.', 'كلمتا السر مش متطابقتين.')}
        </p>
      ) : null}
      {mutation.isError ? (
        <p className="form-error" role="alert">
          {getErrorMessage(
            mutation.error,
            text('The password could not be saved.', 'ما قدرناش نحفظ كلمة السر.'),
            arabic ? 'ar' : 'en',
          )}
        </p>
      ) : null}
      <Button
        className="mt-6 w-full"
        size="lg"
        disabled={invalid || mismatch || confirm.length === 0 || mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending
          ? text('Protecting account…', 'بنأمّن حسابك…')
          : text('Save password and open my profile', 'احفظ كلمة السر وافتح ملفي المهني')}
      </Button>
    </div>
  );
}

export default PasswordSetupPage;
